import {DialogBody, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import styled, {css} from 'styled-components'

export const StyledBulkSiarExportDialogBody = styled(DialogBody)(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing * 4}px;
    `
)

export const StyledBulkSiarExportDialogFooter = styled(DialogFooter)(
    ({theme: {spacing}}) => css`
        display: flex;
        gap: ${spacing * 2}px;
        justify-content: flex-end;
    `
)

export const StyledBulkSiarExportRadioGroup = styled.div(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: row;
        gap: ${spacing * 4}px;
    `
)

export const StyledBulkSiarExportDateRow = styled.div(
    ({theme: {spacing}}) => css`
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: ${spacing * 3}px;
    `
)

export const StyledBulkSiarExportRegistryRow = styled.div`
    display: flex;
    align-items: center;
`

/* Preview step */

export const StyledBulkSiarExportPreviewBody = styled(DialogBody)(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing * 3}px;
        padding-top: ${spacing * 2}px;
    `
)

export const StyledBulkSiarExportCriteriaSummary = styled.div(
    ({theme: {spacing, palette, typography}}) => css`
        ${typography.textSm};
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: ${spacing * 2}px;
        padding: ${spacing * 2}px ${spacing * 3}px;
        border-radius: 8px;
        background: ${palette.neutral[50]};
        border: 1px solid ${palette.neutral[200]};
        color: ${palette.neutral[700]};
    `
)

export const StyledBulkSiarExportCriteriaItem = styled.div(
    ({theme: {spacing, palette}}) => css`
        display: inline-flex;
        align-items: center;
        gap: ${spacing}px;

        & > span:first-child {
            color: ${palette.neutral[500]};
        }

        & > span:last-child {
            color: ${palette.neutral[900]};
            font-weight: 600;
        }
    `
)

export const StyledBulkSiarExportCriteriaDivider = styled.span(
    ({theme: {palette}}) => css`
        color: ${palette.neutral[300]};
        user-select: none;
    `
)

export const StyledBulkSiarExportFilterTabBar = styled.div(
    ({theme: {spacing, palette}}) => css`
        display: flex;
        gap: ${spacing * 2}px;
        border-bottom: 1px solid ${palette.neutral[200]};
    `
)

export const StyledBulkSiarExportFilterTab = styled.button<{$active: boolean}>(
    ({theme: {spacing, palette, typography}, $active}) => css`
        ${typography.textSm};
        background: transparent;
        border: none;
        cursor: pointer;
        padding: ${spacing * 2}px ${spacing * 3}px;
        color: ${$active ? palette.primary[700] : palette.neutral[600]};
        font-weight: ${$active ? 600 : 500};
        border-bottom: 2px solid ${$active ? palette.primary[600] : 'transparent'};
        margin-bottom: -1px;

        &:hover {
            color: ${palette.primary[700]};
        }
    `
)

export const StyledBulkSiarExportPreviewCounterRow = styled.div(
    ({theme: {palette, typography}}) => css`
        ${typography.textSm};
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${palette.neutral[700]};

        & strong {
            font-weight: 600;
        }
    `
)

export const StyledBulkSiarExportToggleAllButton = styled.button(
    ({theme: {palette, typography}}) => css`
        ${typography.textSm};
        background: transparent;
        border: none;
        cursor: pointer;
        color: ${palette.primary[700]};
        font-weight: 600;

        &:hover {
            text-decoration: underline;
        }
    `
)

export const StyledBulkSiarExportPreviewTableWrapper = styled.div(
    ({theme: {palette}}) => css`
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        border: 1px solid ${palette.neutral[200]};
        background-color: ${palette.neutral.white};
        isolation: isolate;
    `
)

/* min-width = sum of column sizes (720px) + TableHeader/TableBodyRow horizontal padding (2 × 24px = 48px) */
export const StyledBulkSiarExportPreviewTableInner = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 768px;
`

export const StyledBulkSiarExportInOtherBatchIcon = styled.span(
    ({theme: {palette}}) => css`
        display: inline-flex;
        align-items: center;
        color: ${palette.orange[500]};
    `
)

export const StyledBulkSiarExportPreviewVirtuosoBody = styled.div`
    height: 380px;
`

export const StyledBulkSiarExportPreviewSkeletonBody = styled.div(
    ({theme: {palette}}) => css`
        display: flex;
        flex-direction: column;
        height: 380px;
        overflow: hidden;

        & > * + * {
            border-top: 1px solid ${palette.neutral[100]};
        }
    `
)

/* Other batch banner */

export const StyledBulkSiarExportOtherBatchBanner = styled.div(
    ({theme: {spacing, palette, typography}}) => css`
        ${typography.textSm};
        display: flex;
        flex-direction: column;
        gap: ${spacing * 2}px;
        padding: ${spacing * 3}px;
        border-radius: 8px;
        background: ${palette.orange[50]};
        border: 1px solid ${palette.orange[200]};
        color: ${palette.orange[900]};

        & strong {
            font-weight: 600;
        }
    `
)

export const StyledBulkSiarExportOtherBatchRadioGroup = styled.div(
    ({theme: {spacing}}) => css`
        display: flex;
        gap: ${spacing * 4}px;
    `
)

export const StyledBulkSiarExportOtherBatchRadioLabel = styled.label(
    ({theme: {spacing, typography}}) => css`
        ${typography.textSm};
        display: flex;
        align-items: center;
        gap: ${spacing * 2}px;
        cursor: pointer;
        font-weight: 500;

        &:has(input:disabled) {
            opacity: 0.6;
            cursor: not-allowed;
        }
    `
)

/* Preview footer with back/export buttons */

export const StyledBulkSiarExportPreviewFooter = styled(DialogFooter)(
    ({theme: {spacing}}) => css`
        display: flex;
        gap: ${spacing * 2}px;
        justify-content: space-between;
        align-items: center;
    `
)

export const StyledBulkSiarExportBackButton = styled.button(
    ({theme: {palette, typography, spacing}}) => css`
        ${typography.textSm};
        display: inline-flex;
        align-items: center;
        gap: ${spacing}px;
        background: transparent;
        border: none;
        cursor: pointer;
        color: ${palette.neutral[700]};
        font-weight: 600;
        padding: ${spacing}px ${spacing * 2}px;

        &:hover {
            color: ${palette.neutral[900]};
        }
    `
)

/* Result step */

export const StyledBulkSiarExportResultBody = styled(DialogBody)(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: ${spacing * 2}px;
        padding: ${spacing * 6}px ${spacing * 4}px;
        text-align: center;
    `
)

export const StyledBulkSiarExportResultIcon = styled.div(
    ({theme: {palette}}) => css`
        display: inline-flex;
        align-items: center;
        justify-content: center;

        svg {
            color: ${palette.green['500']};
            fill: ${palette.green['500']};
        }
    `
)

export const StyledBulkSiarExportResultTitle = styled.h3(
    ({theme: {palette, typography}}) => css`
        ${typography.displayXs};
        font-weight: 600;
        color: ${palette.neutral[900]};
        margin: 0;
    `
)

export const StyledBulkSiarExportResultFileName = styled.p(
    ({theme: {palette, typography}}) => css`
        ${typography.textMd};
        font-weight: 500;
        color: ${palette.neutral[800]};
        margin: 0;
        word-break: break-all;
        text-align: center;
    `
)

export const StyledBulkSiarExportResultSubtitle = styled.p(
    ({theme: {palette, typography}}) => css`
        ${typography.textSm};
        color: ${palette.neutral[600]};
        margin: 0;
    `
)

/* Loading / empty states for preview */

export const StyledBulkSiarExportPreviewEmptyState = styled.div(
    ({theme: {spacing, palette, typography}}) => css`
        ${typography.textSm};
        display: flex;
        align-items: center;
        justify-content: center;
        padding: ${spacing * 8}px ${spacing * 4}px;
        color: ${palette.neutral[600]};
    `
)
